import { useState } from "react";
import { BookAudio, Volume2 } from "lucide-react";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import PronunciationModal from "@/components/agent/modals/configuration/PronunciationModal";
import { Button } from "@/components/ui/button";
import { Agent } from "@/types/agent/agent";

interface PronunciationsCardProps {
  agent: Agent;
  brand?: string;
}

export const PronunciationsCard = ({ agent, brand = "#4E9CFF" }: PronunciationsCardProps) => {
  const [open, setOpen] = useState(false);
  const pronunciations = agent.settings?.pronunciations ?? [];

  return (
    <>
      <OverviewSectionCard
        title="Pronunciations"
        subtitle="Custom word pronunciations for your genie"
        genieIcon="sound-wave"
        brand={brand}
        secondaryColor={agent.branding?.secondaryColor}
        gradientEnabled={agent.branding?.gradientEnabled}
        gradientAngle={agent.branding?.gradientAngle}
      >
        <div className="space-y-4">
          {pronunciations.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Volume2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium text-gray-900 mb-2">No custom pronunciations</p>
              <p className="text-sm text-gray-600">Add pronunciations to control how your genie speaks certain words</p>
            </div>
          ) : (
            <div className="grid gap-2" style={{
              gridTemplateColumns: (() => {
                const longest = Math.max(...pronunciations.map((p: any) => (p.word + p.pronunciation).length));
                const minWidth = Math.min(Math.max(longest * 7 + 80, 160), 500);
                return `repeat(auto-fill, minmax(${minWidth}px, 1fr))`;
              })()
            }}>
              {pronunciations.map((p: any, i: number) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm min-w-0">
                  <BookAudio className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                  <span className="font-medium text-gray-900">{p.word}</span>
                  <span className="text-gray-400 shrink-0">→</span>
                  <span className="text-gray-600">{p.pronunciation}</span>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2 ml-auto">
            <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="shrink-0 gap-1.5">
              <BookAudio className="w-3.5 h-3.5" />
              Manage
            </Button>
          </div>
        </div>
      </OverviewSectionCard>

      <PronunciationModal genie={agent} isOpen={open} onClose={() => setOpen(false)} />
    </>
  );
};
